import { Stepper } from '@mui/material';
import Button from '@mui/material/Button';
import Step from '@mui/material/Step';
import StepContent from '@mui/material/StepContent';
import StepLabel from '@mui/material/StepLabel';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { createRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { type ImportAnalysis, ImportService } from '~/services/import';

const CustomStepper = styled(Stepper)({
  margin: '4px',
});

interface StepperButtonsProps {
  activeStep: any;
  setActiveStep: any;
  loading: boolean;
  error: boolean;
}

const StepperButtons = (props: StepperButtonsProps) => {
  return (
    <div>
      <div>
        <Button
          disabled={props.activeStep < 1 || props.activeStep > 1 || props.loading}
          onClick={() => props.setActiveStep(props.activeStep - 1)}
        >
          Tillbaka
        </Button>
        <Button type="submit" color={'primary'} disabled={props.error || props.loading}>
          Fortsätt
        </Button>
      </div>
    </div>
  );
};

interface SelectedFilesProps {
  file:
    | {
        FileList: {
          name: string;
        };
      }
    | undefined;
}

const SelectedFile = (props: SelectedFilesProps) => {
  if (props.file !== undefined) {
    const files: Array<any> = [];
    for (const [key, value] of Object.entries(props.file)) {
      files.push(<Typography key={key}> Filnamn: {value && value.name} </Typography>);
    }
    return <>{files}</>;
  } else {
    return <Typography />;
  }
};

interface AnalysisProps {
  analysis: Array<ImportAnalysis>;
}

const TableRows = (props: AnalysisProps) => {
  const rows: Array<any> = [];
  for (const [key, value] of Object.entries(props.analysis)) {
    if (value.error.length <= 0) {
      rows.push(
        <TableRow key={key}>
          <TableCell>{value.fileName}</TableCell>
          <TableCell>{value.importType}</TableCell>
          <TableCell>{value.submission}</TableCell>
          <TableCell>{value.fileIdentity}</TableCell>
          <TableCell>{value.dataRows}</TableCell>
        </TableRow>
      );
    } else {
      rows.push(
        <TableRow key={key}>
          <TableCell>{value.fileName}</TableCell>
          <TableCell colSpan={3}>
            {value.error.split('\n').map((item) => (
              <Typography color="error">{item}</Typography>
            ))}
          </TableCell>
          <TableCell />
        </TableRow>
      );
    }
  }
  return <>{rows}</>;
};

const checkErrors = (props: Array<ImportAnalysis>) => {
  let errs = false;
  if (props !== undefined) {
    for (const value of Object.values(props)) {
      if (value.error.length > 0) {
        errs = true;
      }
    }
  }
  return errs;
};

const PressBack = (props: AnalysisProps) => {
  if (checkErrors(props.analysis)) {
    return <>{<Typography>Tryck på tillbaka och gör ändringarna och ladda upp filen på nytt.</Typography>}</>;
  } else {
    return <></>;
  }
};

export default function ImportStepper(props: any) {
  const [activeStep, setActiveStep] = useState(0);
  const [analysis, setAnalysis] = useState([
    {
      fileName: '',
      importType: '',
      submission: '',
      fileIdentity: '',
      dataRows: 0,
      error: '',
    },
  ]);
  const [loading, setLoading] = useState(false);
  const [file, setFile] = useState(undefined);
  const fileRef: any = createRef();

  const handleSubmit = (event: any) => {
    event.preventDefault();
    switch (activeStep) {
      case 0:
        setLoading(true);
        ImportService.analyze(file).subscribe((data: Array<ImportAnalysis>) => {
          setLoading(false);
          setAnalysis(data);
          setActiveStep(activeStep + 1);
        });
        break;
      case 1:
        setLoading(true);
        ImportService.importFile(file).subscribe(() => {
          setLoading(false);
          setActiveStep(activeStep + 1);
        });
        break;
    }
  };

  const onFileChange = () => {
    if (fileRef.current.files && fileRef.current.files.length > 0) {
      setFile(fileRef.current.files);
    } else {
      setFile(undefined);
    }
  };

  const useStyles = makeStyles()({
    label: {
      padding: '4px',
      margin: '4px 0',
      border: 'black 1px solid',
      cursor: 'pointer',
    },
  });

  const { classes } = useStyles();

  return (
    <form onSubmit={handleSubmit}>
      <input hidden={true} type="file" multiple ref={fileRef} id="importfile" onChange={onFileChange} />
      <CustomStepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>Importera fil</StepLabel>
          <StepContent>
            <Typography>
              Välj en fil och klicka sedan på fortsätt. Innan importen görs kommer du få en kort sammanfattning av vad
              filen innehåller.
            </Typography>

            {loading && <Typography>Laddar...</Typography>}

            <label className={classes.label} htmlFor="importfile">
              Välj fil
            </label>
            <SelectedFile file={file} />

            <StepperButtons
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              loading={loading}
              error={file === undefined}
            />
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Snabbanalys av importfilen</StepLabel>
          <StepContent>
            <Typography>
              Här finns möjlighet att gå igenom ändringarna och om det skett några fel så fås den informationen här.
            </Typography>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Filnamn</TableCell>
                  <TableCell>Importtyp</TableCell>
                  <TableCell>Submissionsår</TableCell>
                  <TableCell>Filidentifierare</TableCell>
                  <TableCell>Rader av data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <TableRows analysis={analysis} />
              </TableBody>
            </Table>
            <PressBack analysis={analysis} />
            {loading && <Typography>Laddar...</Typography>}
            <StepperButtons
              activeStep={activeStep}
              setActiveStep={setActiveStep}
              loading={loading}
              error={checkErrors(analysis)}
            />
          </StepContent>
        </Step>
        <Step>
          <StepLabel>Importen bekräftad</StepLabel>
          <StepContent>
            <Typography>
              Importen bekräftad och lagd i importkön. Notera att fel fortfarande kan hittas i filen när importen körs
              på riktigt.
            </Typography>
            <Typography>Du kan gå tillbaka till Importkön för att se statusen på den här importen.</Typography>
            <Link to="/imports">Importkö</Link>
          </StepContent>
        </Step>
      </CustomStepper>
    </form>
  );
}
